<template>
  <div
    ref="container"
    class="block-ui-wrapper"
  >
    <slot />
    <transition name="fade-block-ui">
      <div
        v-if="blocked"
        class="block-ui"
        :style="overlayStyles"
      >
        <Icon
          v-if="showSpinner"
          type="spinner-third"
          animation="spin"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type { ComputedRef, CSSProperties } from 'vue'
import Icon from '@/components/icon/Icon.vue'

defineProps({
  blocked: {
    type: Boolean,
    default: false
  },
  showSpinner: {
    type: Boolean,
    default: true
  }
})

const container = ref<HTMLElement | null>(null)
const width = ref(0)
const height = ref(0)

const overlayStyles: ComputedRef<CSSProperties> = computed(() => ({
  width: `${width.value}px`,
  height: `${height.value}px`
}))

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  nextTick(() => {
    if (container.value) {
      updateSize()
      resizeObserver = new ResizeObserver(updateSize)
      resizeObserver.observe(container.value)
    }
  })
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

function updateSize() {
  if (container.value) {
    width.value = container.value.offsetWidth
    height.value = container.value.offsetHeight
  }
}
</script>

<style scoped>
.block-ui-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.block-ui {
  position: absolute;
  top: 0;
  left: 0;
  background-color: color-mix(in srgb, var(--bg-primary) 50%, transparent);
  backdrop-filter: blur(2px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--rounded-xl);
  color: var(--text-tertiary);
  font-size: 2.1rem;
}

/* Transition classes */
.fade-block-ui-enter-active,
.fade-block-ui-leave-active {
  transition: opacity 0.2s ease;
}
.fade-block-ui-enter-from,
.fade-block-ui-leave-to {
  opacity: 0;
}
</style>
